// エラータイプの定義
export enum ErrorType {
  RATE_LIMIT = 'RATE_LIMIT',
  NETWORK = 'NETWORK',
  JSON_PARSE = 'JSON_PARSE',
  TOKEN_LIMIT = 'TOKEN_LIMIT',
  API_KEY = 'API_KEY',
  UNKNOWN = 'UNKNOWN'
}

// エラー分析関数
export function analyzeError(error: any): ErrorType {
  const message = error?.message || error?.toString() || ''
  
  if (message.includes('429') || message.includes('rate') || message.includes('quota')) {
    return ErrorType.RATE_LIMIT
  }
  if (message.includes('JSON') || message.includes('parse')) {
    return ErrorType.JSON_PARSE
  }
  if (message.includes('token') || message.includes('length')) {
    return ErrorType.TOKEN_LIMIT
  }
  if (message.includes('fetch') || message.includes('network') || message.includes('Failed to fetch')) {
    return ErrorType.NETWORK
  }
  if (message.includes('API') || message.includes('key') || message.includes('401')) {
    return ErrorType.API_KEY
  }
  
  return ErrorType.UNKNOWN
}

// ユーザーフレンドリーなエラーメッセージ生成
export function getErrorMessage(errorType: ErrorType, details?: string): {
  title: string
  message: string
  action?: string
} {
  switch (errorType) {
    case ErrorType.RATE_LIMIT:
      return {
        title: 'APIレート制限',
        message: 'APIの利用制限に達しました。',
        action: '別のAPIキーに切り替えるか、しばらく待ってから再試行してください。'
      }
    
    case ErrorType.NETWORK:
      return {
        title: 'ネットワークエラー',
        message: 'サーバーとの通信に失敗しました。',
        action: 'インターネット接続を確認して、もう一度お試しください。'
      }
    
    case ErrorType.JSON_PARSE:
      return {
        title: '応答解析エラー',
        message: 'AIからの応答の解析に失敗しました。',
        action: '生成設定を簡略化するか、もう一度お試しください。'
      }
    
    case ErrorType.TOKEN_LIMIT:
      return {
        title: 'コンテンツ制限',
        message: '生成内容が長すぎます。',
        action: '入力内容を短くするか、設定を簡略化してください。'
      }
    
    case ErrorType.API_KEY:
      return {
        title: 'APIキーエラー',
        message: 'APIキーが無効または未設定です。',
        action: '設定画面でAPIキーを確認してください。'
      }
    
    case ErrorType.UNKNOWN:
    default:
      return {
        title: 'エラーが発生しました',
        message: details || '予期しないエラーが発生しました。',
        action: 'もう一度お試しください。問題が続く場合は、設定を確認してください。'
      }
  }
}

// リトライ設定
interface RetryOptions {
  maxRetries?: number
  initialDelay?: number
  maxDelay?: number
  backoffFactor?: number
  onRetry?: (attempt: number, delay: number) => void
}

// 指数バックオフでリトライ
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 30000,
    backoffFactor = 2,
    onRetry
  } = options
  
  let lastError: any
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error
      
      // 最後の試行の場合はエラーをスロー
      if (attempt === maxRetries - 1) {
        throw error
      }
      
      // エラータイプを分析
      const errorType = analyzeError(error)
      
      // リトライ不可能なエラーの場合は即座にスロー
      if (errorType === ErrorType.API_KEY) {
        throw error
      }
      
      // 遅延時間を計算（指数バックオフ）
      const delay = Math.min(
        initialDelay * Math.pow(backoffFactor, attempt),
        maxDelay
      )
      
      // リトライコールバック
      if (onRetry) {
        onRetry(attempt + 1, delay)
      }
      
      // 遅延
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
  
  throw lastError
}

// APIキー自動切り替え機能（無効化）
// 複数のAPIキー管理が複雑になるため、自動切り替えは行わない
export async function handleApiKeyRotation(error: any): Promise<boolean> {
  // 自動切り替えは行わない
  return false
}

// 生成エラーの詳細ログ
export function logGenerationError(
  generator: string,
  error: any,
  context?: Record<string, any>
) {
  const errorType = analyzeError(error)
  const timestamp = new Date().toISOString()
  
  console.group(`[${generator} Generator Error] ${timestamp}`)
  console.error('Error Type:', errorType)
  console.error('Error Message:', error?.message || error)
  console.error('Error Stack:', error?.stack)
  
  if (context) {
    console.log('Context:', context)
  }
  
  console.groupEnd()
}